/**
 * IndexedDB Store for Offline POS Operations
 * Uses Dexie.js for robust IndexedDB handling
 */
import Dexie from 'dexie';
import { v4 as uuidv4 } from 'uuid';

// Database definition
class POSDatabase extends Dexie {
  constructor() {
    super('PHTabletPOS');
    
    this.version(1).stores({
      // Offline sales queue
      offlineSales: 'local_id, idempotency_key, sync_status, created_at_local',
      
      // Receipt number mapping (offline -> server)
      receiptMappings: 'offline_receipt_number, server_receipt_number',
      
      // Cached data (read-only when offline)
      cachedProducts: 'id, organization_id, category_id, barcode, sku',
      cachedCategories: 'id, organization_id',
      cachedSettings: 'key',
      
      // Sync state
      syncState: 'key'
    });
  }
}

export const db = new POSDatabase();

// ============ SYNC STATUS ENUM ============
export const SyncStatus = {
  PENDING: 'PENDING',
  SYNCING: 'SYNCING',
  SYNCED: 'SYNCED',
  FAILED: 'FAILED'
};

// ============ OFFLINE SALES QUEUE ============

/**
 * Generate offline receipt number
 * Format: OFF-{branchCode}-{YYYYMMDD}-{shortUUID}
 */
export function generateOfflineReceiptNumber(branchCode) {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const shortId = uuidv4().slice(0, 8);
  return `OFF-${branchCode}-${dateStr}-${shortId}`;
}

/**
 * Queue an offline sale
 */
export async function queueOfflineSale(saleData) {
  const localId = uuidv4();
  const idempotencyKey = `sale_${localId}`;
  const createdAt = new Date().toISOString();
  
  const offlineSale = {
    local_id: localId,
    idempotency_key: idempotencyKey,
    tenant_id: saleData.tenantId,
    branch_id: saleData.branchId,
    cashier_id: saleData.cashierId,
    created_at_local: createdAt,
    offline_receipt_number: saleData.offlineReceiptNumber,
    items: saleData.items.map(item => ({
      product_id: item.product_id || item.productId,
      product_name: item.product_name || item.productName || item.name,
      unit_price: item.unit_price || item.price,
      quantity: item.quantity,
      line_discount: item.line_discount || item.discount || 0,
      notes: item.notes || null
    })),
    payment_method: saleData.paymentMethod,
    payment_reference: saleData.paymentReference || null,
    tendered_amount: saleData.tenderedAmount,
    discount_total: saleData.discountTotal || 0,
    discount_type: saleData.discountType || 'regular',
    discount_customer_name: saleData.discountCustomerName || null,
    discount_customer_id: saleData.discountCustomerId || null,
    discount_remarks: saleData.discountRemarks || null,
    subtotal: saleData.subtotal,
    vat_amount: saleData.vatAmount || 0,
    grand_total: saleData.grandTotal,
    change_amount: saleData.changeAmount || 0,
    sync_status: SyncStatus.PENDING,
    last_error: null,
    retry_count: 0
  };
  
  await db.offlineSales.add(offlineSale);
  
  return {
    localId,
    idempotencyKey,
    offlineReceiptNumber: saleData.offlineReceiptNumber,
    createdAt
  };
}

/**
 * Get all pending offline sales
 */
export async function getPendingOfflineSales() {
  return db.offlineSales
    .where('sync_status')
    .equals(SyncStatus.PENDING)
    .toArray();
}

/**
 * Get offline sales count by status
 */
export async function getOfflineSalesCount() {
  const pending = await db.offlineSales
    .where('sync_status')
    .equals(SyncStatus.PENDING)
    .count();
  
  const failed = await db.offlineSales
    .where('sync_status')
    .equals(SyncStatus.FAILED)
    .count();
  
  return { pending, failed };
}

/**
 * Update sale sync status
 */
export async function updateSaleSyncStatus(localId, status, error = null) {
  await db.offlineSales.update(localId, {
    sync_status: status,
    last_error: error,
    retry_count: status === SyncStatus.FAILED 
      ? (await db.offlineSales.get(localId))?.retry_count + 1 || 1
      : undefined
  });
}

/**
 * Mark sale as synced and store receipt mapping
 */
export async function markSaleAsSynced(localId, serverSaleId, serverReceiptNumber, offlineReceiptNumber) {
  await db.transaction('rw', [db.offlineSales, db.receiptMappings], async () => {
    await db.offlineSales.update(localId, {
      sync_status: SyncStatus.SYNCED,
      server_sale_id: serverSaleId,
      server_receipt_number: serverReceiptNumber,
      synced_at: new Date().toISOString()
    });
    
    // Store receipt mapping for audit
    await db.receiptMappings.put({
      offline_receipt_number: offlineReceiptNumber,
      server_receipt_number: serverReceiptNumber,
      server_sale_id: serverSaleId,
      synced_at: new Date().toISOString()
    });
  });
}

/**
 * Clear synced sales (keep for 7 days for audit)
 */
export async function clearOldSyncedSales() {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - 7);
  
  await db.offlineSales
    .where('sync_status')
    .equals(SyncStatus.SYNCED)
    .and(sale => new Date(sale.synced_at) < cutoffDate)
    .delete();
}

// ============ CACHED DATA ============

/**
 * Cache products from server
 */
export async function cacheProducts(products) {
  await db.transaction('rw', db.cachedProducts, async () => {
    await db.cachedProducts.clear();
    await db.cachedProducts.bulkAdd(products);
  });
  
  await updateCacheTimestamp('products');
}

/**
 * Get cached products
 */
export async function getCachedProducts(organizationId) {
  if (organizationId) {
    return db.cachedProducts
      .where('organization_id')
      .equals(organizationId)
      .toArray();
  }
  return db.cachedProducts.toArray();
}

/**
 * Cache categories from server
 */
export async function cacheCategories(categories) {
  await db.transaction('rw', db.cachedCategories, async () => {
    await db.cachedCategories.clear();
    await db.cachedCategories.bulkAdd(categories);
  });
  
  await updateCacheTimestamp('categories');
}

/**
 * Get cached categories
 */
export async function getCachedCategories(organizationId) {
  if (organizationId) {
    return db.cachedCategories
      .where('organization_id')
      .equals(organizationId)
      .toArray();
  }
  return db.cachedCategories.toArray();
}

/**
 * Cache settings
 */
export async function cacheSettings(key, data) {
  await db.cachedSettings.put({
    key,
    data,
    cached_at: new Date().toISOString()
  });
}

/**
 * Get cached settings
 */
export async function getCachedSettings(key) {
  const record = await db.cachedSettings.get(key);
  return record?.data || null;
}

// ============ SYNC STATE ============

async function updateCacheTimestamp(key) {
  await db.syncState.put({
    key: `cache_${key}`,
    timestamp: new Date().toISOString()
  });
}

export async function getCacheTimestamp(key) {
  const record = await db.syncState.get(`cache_${key}`);
  return record?.timestamp || null;
}

export async function getLastSyncTimestamp() {
  const record = await db.syncState.get('last_sync');
  return record?.timestamp || null;
}

export async function updateLastSyncTimestamp() {
  await db.syncState.put({
    key: 'last_sync',
    timestamp: new Date().toISOString()
  });
}

// ============ DATABASE UTILITIES ============

/**
 * Clear all cached data (for logout)
 */
export async function clearAllCaches() {
  await db.transaction('rw', [db.cachedProducts, db.cachedCategories, db.cachedSettings, db.syncState], async () => {
    await db.cachedProducts.clear();
    await db.cachedCategories.clear();
    await db.cachedSettings.clear();
    await db.syncState.clear();
  });
}

/**
 * Get database stats
 */
export async function getDatabaseStats() {
  const [products, categories, pendingSales, syncedSales] = await Promise.all([
    db.cachedProducts.count(),
    db.cachedCategories.count(),
    db.offlineSales.where('sync_status').equals(SyncStatus.PENDING).count(),
    db.offlineSales.where('sync_status').equals(SyncStatus.SYNCED).count()
  ]);
  
  return {
    cachedProducts: products,
    cachedCategories: categories,
    pendingSales,
    syncedSales
  };
}

export default db;
